/**
 * Client-side customization preview export (optional).
 * Primary preview generation runs server-side; this module supports
 * uploading a client-rendered WebP when canvas CORS allows it.
 */
(function (global) {
  'use strict';

  var CANVAS_REF = 720;
  var EXPORT_SIZE = 1440;

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('Could not load product image for preview export'));
      };
      img.src = url;
    });
  }

  function waitForFonts(zones) {
    if (!global.document || !document.fonts || !document.fonts.load) {
      return Promise.resolve();
    }
    var jobs = [];
    (zones || []).forEach(function (z) {
      var fam = String((z && z.fontFamily) || '')
        .replace(/['"]/g, '')
        .split(',')[0]
        .trim();
      if (fam) jobs.push(document.fonts.load('16px "' + fam + '"'));
    });
    return Promise.all(jobs).catch(function () {});
  }

  function measureWrappedLines(ctx, text, maxWidth) {
    var content = String(text || '');
    if (!content) return [];
    if (content.indexOf('\n') !== -1) {
      return content
        .split('\n')
        .map(function (line) {
          return line.trim();
        })
        .filter(Boolean);
    }

    var words = content.split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    // Keep slash-dates / single tokens on one line when they fit.
    if (words.length === 1) {
      return [words[0]];
    }

    var lines = [];
    var line = '';
    words.forEach(function (word) {
      var test = line ? line + ' ' + word : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    });
    if (line) lines.push(line);
    return lines;
  }

  function fitFontSize(ctx, text, maxWidth, maxHeight, maxFont, weight, style, fontFamily) {
    var upper = Math.max(6, Number(maxFont) || 32);
    var minFont = Math.max(6, upper * 0.35);
    var maxSteps = Math.min(360, Math.max(0, Math.ceil((upper - minFont) / 0.5)));

    function overflows(step) {
      var size = upper - step * 0.5;
      ctx.font = style + ' ' + weight + ' ' + size + 'px ' + fontFamily;
      var lines = measureWrappedLines(ctx, text, maxWidth);
      var lineHeight = size * 1.15;
      var blockH = Math.max(lineHeight, lines.length * lineHeight);
      var widest = 0;
      lines.forEach(function (entry) {
        widest = Math.max(widest, ctx.measureText(entry).width);
      });
      return widest > maxWidth + 1 || blockH > maxHeight + 1;
    }

    if (!overflows(0)) return upper;
    var low = 0;
    var high = maxSteps;
    while (low < high) {
      var middle = Math.floor((low + high) / 2);
      if (overflows(middle)) low = middle + 1;
      else high = middle;
    }
    return upper - low * 0.5;
  }

  function drawWrappedText(ctx, text, x, y, maxWidth, maxHeight, lineHeight) {
    var lines = measureWrappedLines(ctx, text, maxWidth);
    var totalHeight = lines.length * lineHeight;
    var startY = y + Math.max(0, (maxHeight - totalHeight) / 2) + lineHeight / 2;
    lines.forEach(function (entry, index) {
      ctx.fillText(entry, x, startY + index * lineHeight);
    });
  }

  function drawZone(ctx, z, size) {
    var x = ((Number(z.x) || 0) / 100) * size;
    var y = ((Number(z.y) || 0) / 100) * size;
    var w = Math.max(1, ((Number(z.w) || 0) / 100) * size);
    var h = Math.max(1, ((Number(z.h) || 0) / 100) * size);
    var cx = x + w / 2;
    var cy = y + h / 2;
    var rotation = ((Number(z.rotation) || 0) * Math.PI) / 180;
    var maxFont = (Number(z.fontSize) || 32) * (size / CANVAS_REF);
    var opacity = z.opacity == null ? 1 : Number(z.opacity) / 100;
    var fontFamily = z.fontFamily || 'Arial, sans-serif';
    var weight = z.bold ? '700' : '400';
    var style = z.italic ? 'italic' : 'normal';

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    // Clip to zone box so rotated text cannot bake into neighboring zones.
    ctx.beginPath();
    ctx.rect(-w / 2, -h / 2, w, h);
    ctx.clip();
    ctx.globalAlpha = opacity;

    var fontSize = fitFontSize(ctx, z.text || '', w, h, maxFont, weight, style, fontFamily);
    ctx.font = style + ' ' + weight + ' ' + fontSize + 'px ' + fontFamily;
    ctx.fillStyle = z.color || '#1b2430';

    var align = z.align || 'center';
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    var textX = align === 'left' ? -w / 2 : align === 'right' ? w / 2 : 0;
    drawWrappedText(ctx, z.text || '', textX, -h / 2, w, h, fontSize * 1.15);
    ctx.restore();
  }

  function exportPreview(options) {
    options = options || {};
    var backgroundUrl = options.backgroundUrl;
    var zones = options.zones || [];
    var size = Number(options.size) || EXPORT_SIZE;
    var quality = Number(options.quality) || 0.92;

    if (!backgroundUrl) {
      return Promise.reject(new Error('backgroundUrl required'));
    }

    return waitForFonts(zones)
      .then(function () {
        return loadImage(backgroundUrl);
      })
      .then(function (bg) {
        var canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        var ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas not supported');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        var scale = Math.min(size / bg.width, size / bg.height);
        var dw = bg.width * scale;
        var dh = bg.height * scale;
        var dx = (size - dw) / 2;
        var dy = (size - dh) / 2;
        ctx.drawImage(bg, dx, dy, dw, dh);

        zones.forEach(function (z) {
          if (z && z.text) drawZone(ctx, z, size);
        });

        return new Promise(function (resolve, reject) {
          canvas.toBlob(
            function (blob) {
              if (blob) resolve(blob);
              else reject(new Error('WebP export failed — browser may block cross-origin image'));
            },
            'image/webp',
            quality
          );
        });
      });
  }

  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var result = String(reader.result || '');
        resolve(result.split(',')[1] || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  global.HgPreviewExport = {
    CANVAS_REF: CANVAS_REF,
    EXPORT_SIZE: EXPORT_SIZE,
    exportPreview: exportPreview,
    blobToBase64: blobToBase64,
  };
})(typeof window !== 'undefined' ? window : globalThis);
